import { Component } from '@theme/component';

/**
 * SD Slider Swiper Component
 *
 * Alternative slider implementation using Swiper.js for enhanced touch handling.
 * Maintains the same API as sd-slider.js for seamless switching via section settings.
 *
 * EXTERNAL DEPENDENCY NOTE: This component uses Swiper.js (~40KB) as an intentional
 * exception to our "zero external dependencies" rule. Swiper provides significantly
 * better touch/drag handling (momentum, two-finger trackpad gestures, etc.) that
 * cannot be replicated with native CSS scroll-snap alone. This is opt-in via the
 * section's "Slider engine" setting; native scroll-snap remains the default.
 *
 * @typedef {Object} SDSliderSwiperRefs
 * @property {HTMLElement} scroller - Swiper container (.swiper)
 * @property {HTMLElement[]} slide - Array of slide elements
 * @property {HTMLButtonElement[]} [dot] - Array of dot navigation buttons
 * @property {HTMLButtonElement} [prev] - Previous button
 * @property {HTMLButtonElement} [next] - Next button
 * @property {HTMLElement} [liveRegion] - ARIA live region for announcements
 *
 * @extends {Component<SDSliderSwiperRefs>}
 */
class SDSliderSwiper extends Component {
  requiredRefs = ['scroller'];

  /** @type {import('swiper').Swiper|null} */
  #swiper = null;

  /** @type {AbortController|null} */
  #abortController = null;

  /** @type {boolean} */
  #isPaused = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();

    const slides = this.refs.slide;
    if (!this.refs.scroller || !slides?.length) return;

    // Wait for Swiper to be available (loaded via separate script tag)
    if (typeof window.Swiper === 'undefined') {
      console.error('SDSliderSwiper: Swiper library not loaded');
      return;
    }

    // Skip autoplay if user prefers reduced motion
    const prefersReducedMotion = this.#prefersReducedMotion();

    this.#initSwiper(prefersReducedMotion);
    this.#bindNavigation();
    this.#setupEventListeners();

    // Initialize button states
    this.#updateDots();
    this.#updateButtonStates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
  }

  // ==========================================================================
  // Getters
  // ==========================================================================

  /**
   * Get autoplay interval in milliseconds
   * @returns {number|null}
   */
  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    if (!interval) return null;
    const value = parseInt(interval, 10);
    return isNaN(value) ? null : value * 1000;
  }

  /**
   * Check if infinite loop is enabled
   * @returns {boolean}
   */
  get infinite() {
    return this.hasAttribute('infinite');
  }

  /**
   * Check if arrow direction is reversed
   * @returns {boolean}
   */
  get reverseArrows() {
    return this.hasAttribute('reverse-arrows');
  }

  /**
   * Get current slide index
   * @returns {number}
   */
  get currentIndex() {
    return this.#swiper?.realIndex ?? 0;
  }

  // ==========================================================================
  // Public Methods (same interface as sd-slider.js)
  // ==========================================================================

  /**
   * Navigate to a specific slide
   * @param {number} index - Target slide index
   */
  select(index) {
    if (!this.#swiper) return;

    // Enable announcements for manual navigation
    this.refs.liveRegion?.setAttribute('aria-live', 'polite');

    // Swiper handles looping internally when loop: true
    this.#swiper.slideTo(index);
  }

  /**
   * Go to next slide
   * When reverse-arrows is enabled, this moves backward
   */
  next() {
    if (!this.#swiper) return;

    if (this.reverseArrows) {
      this.#swiper.slidePrev();
    } else {
      this.#swiper.slideNext();
    }
  }

  /**
   * Go to previous slide
   * When reverse-arrows is enabled, this moves forward
   */
  previous() {
    if (!this.#swiper) return;

    if (this.reverseArrows) {
      this.#swiper.slideNext();
    } else {
      this.#swiper.slidePrev();
    }
  }

  /**
   * Start autoplay
   */
  play() {
    if (!this.#swiper?.autoplay || this.#prefersReducedMotion()) return;
    this.#isPaused = false;
    this.refs.liveRegion?.setAttribute('aria-live', 'off');
    this.#swiper.autoplay.start();
  }

  /**
   * Pause autoplay
   */
  pause() {
    this.#isPaused = true;
    this.refs.liveRegion?.setAttribute('aria-live', 'polite');
    this.#swiper?.autoplay?.stop();
  }

  /**
   * Resume autoplay if not explicitly paused
   */
  resume() {
    if (this.#isPaused || this.#prefersReducedMotion()) return;
    this.play();
  }

  // ==========================================================================
  // Private Methods
  // ==========================================================================

  /**
   * Check if user prefers reduced motion
   * @returns {boolean}
   */
  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Initialize Swiper instance
   * @param {boolean} prefersReducedMotion
   */
  #initSwiper(prefersReducedMotion) {
    /** @type {import('swiper').SwiperOptions} */
    const swiperConfig = {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: this.infinite,
      speed: prefersReducedMotion ? 0 : 400,
      grabCursor: true,
      watchSlidesProgress: true,
      // Disable Swiper's built-in navigation/pagination - we use our snippets
      navigation: false,
      pagination: false,
    };

    // Autoplay configuration
    if (this.autoplayInterval && !prefersReducedMotion) {
      swiperConfig.autoplay = {
        delay: this.autoplayInterval,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    // Keyboard navigation
    swiperConfig.keyboard = {
      enabled: true,
      onlyInViewport: true,
    };

    // Mousewheel/trackpad support - allows two-finger swipe on trackpads
    swiperConfig.mousewheel = {
      enabled: true,
      forceToAxis: true, // Only respond to horizontal swipes
      sensitivity: 1.5, // Amplify swipe gesture
      thresholdDelta: 10, // Lower threshold for easier triggering
    };

    // Initialize Swiper (accessed via window since it's loaded as a global script)
    // @ts-ignore - Swiper is loaded globally
    this.#swiper = new window.Swiper(this.refs.scroller, swiperConfig);

    // Sync dots and button states on slide change
    this.#swiper.on('slideChange', () => {
      this.#updateDots();
      this.#updateButtonStates();
      this.#announceSlide();
    });

    // Add dragging state attribute for CSS
    this.#swiper.on('sliderMove', () => {
      this.setAttribute('dragging', '');
    });

    this.#swiper.on('touchEnd', () => {
      this.removeAttribute('dragging');
    });
  }

  /**
   * Bind navigation controls to Swiper
   */
  #bindNavigation() {
    if (!this.#abortController) return;
    const { signal } = this.#abortController;
    const { prev, next, dot } = this.refs;

    // Arrow buttons
    prev?.addEventListener('click', () => this.previous(), { signal });
    next?.addEventListener('click', () => this.next(), { signal });

    // Dot buttons
    dot?.forEach((dotEl) => {
      dotEl.addEventListener(
        'click',
        () => {
          const index = parseInt(dotEl.dataset.slideIndex || '0', 10);
          this.select(index);
        },
        { signal }
      );
    });
  }

  /**
   * Setup additional event listeners
   */
  #setupEventListeners() {
    const { signal } = this.#abortController;

    // Visibility change handling for autoplay
    if (this.autoplayInterval) {
      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) {
            this.#swiper?.autoplay?.stop();
          } else {
            this.resume();
          }
        },
        { signal }
      );
    }
  }

  /**
   * Update dot active states
   */
  #updateDots() {
    const dots = this.refs.dot;
    const realIndex = this.#swiper?.realIndex ?? 0;

    dots?.forEach((dot, i) => {
      if (i === realIndex) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  /**
   * Update arrow button disabled states for non-infinite mode
   */
  #updateButtonStates() {
    const { prev, next } = this.refs;
    const slides = this.refs.slide;

    if (this.infinite || !slides?.length) {
      // In loop mode, buttons are never disabled
      if (prev) prev.disabled = false;
      if (next) next.disabled = false;
      return;
    }

    const isBeginning = this.#swiper?.isBeginning ?? true;
    const isEnd = this.#swiper?.isEnd ?? false;

    // When reverse-arrows is enabled, swap the disabled states
    if (this.reverseArrows) {
      if (prev) prev.disabled = isEnd;
      if (next) next.disabled = isBeginning;
    } else {
      if (prev) prev.disabled = isBeginning;
      if (next) next.disabled = isEnd;
    }
  }

  /**
   * Announce current slide to screen readers
   */
  #announceSlide() {
    const { liveRegion } = this.refs;
    const slides = this.refs.slide;
    const realIndex = this.#swiper?.realIndex ?? 0;

    if (liveRegion && slides?.length) {
      liveRegion.textContent = `Slide ${realIndex + 1} of ${slides.length}`;
    }

    // Update aria-hidden on slides
    slides?.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i !== realIndex ? 'true' : 'false');
    });
  }
}

// Register custom element
if (!customElements.get('sd-slider-swiper')) {
  customElements.define('sd-slider-swiper', SDSliderSwiper);
}
